//-- Copyright (c) 2021 TMT Insights Group, LLC
import { DirectLink } from '@/__configs/config_types';
import styled from '@emotion/styled';
import { Button, ButtonProps, Menu, MenuItem } from '@mui/material';
import { SyntheticEvent, useState } from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';
import PolarisIcon from '../_common/PolarisIcon';

type NavDropdownProps = ButtonProps & {
  id: string;
  label: string;
  items: DirectLink[];
  activeStyling: string;
};

const NavDropdown = ({ id, label, items = [], activeStyling }: NavDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event: SyntheticEvent<any>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const match = items.some((item) => !!useRouteMatch(item.to));

  if (items.length <= 0) {
    return null;
  }

  return (
    <div>
      <LabelButton onClick={handleClick} matched={`${!!match}`} disableRipple activestyling={activeStyling}>
        {label}
        <PolarisIcon icon="down" />
      </LabelButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <div>
          {items.map((item, i) => (
            <StyledNavLink key={`${id}-${i}`} exact={item.exact} to={item.to} activeClassName={'active'}>
              <MenuItem key={`${id}-${i}`}>{item.label}</MenuItem>
            </StyledNavLink>
          ))}
        </div>
      </Menu>
    </div>
  );
};

export default NavDropdown;

const LabelButton = styled(Button)<{ matched: string; activestyling: string }>`
  white-space: nowrap;
  text-transform: none;
  letter-spacing: 0;
  position: relative;
  font-size: 1rem;
  opacity: 0.7;
  color: #fff;
  text-decoration: none;
  transition: ease opacity 250ms;
  padding: ${({ theme }) => theme.spacing(1, 3)};
  line-height: 1;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(0.5)};
  height: 34px;

  & svg {
    color: white;
    opacity: 1;
  }

  &.MuiButton-root {
    ${({ matched, activestyling }) => matched === 'true' && activestyling};
  }

  &:hover {
    background-color: transparent;
  }
`;

const StyledNavLink = styled(NavLink)`
  font-size: 0.95rem;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};

  &.active {
    color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.light)};
  }
`;
